import type { StoreApi } from 'zustand';
import type { LayoutState, LayoutSplittersSlice } from './layoutStoreTypes';

const DEFAULT_MATCH_REPLACE_REQUESTS_PREVIEW_SPLITTER_SIZES = [
  { id: 'requests', size: 22, minSize: 14 },
  { id: 'http-preview', size: 78, minSize: 42 },
];

const DEFAULT_MATCH_REPLACE_ORIGINAL_TRANSFORMED_SPLITTER_SIZES = [
  { id: 'original', size: 50, minSize: 28 },
  { id: 'transformed', size: 50, minSize: 28 },
];

const DEFAULT_MATCH_REPLACE_RULES_EDITOR_SPLITTER_SIZES = [
  { id: 'main', size: 72, minSize: 48 },
  { id: 'rule-editor', size: 28, minSize: 22 },
];

const DEFAULT_MATCH_REPLACE_RULES_PREVIEW_SPLITTER_SIZES = [
  { id: 'rules-table', size: 32, minSize: 20 },
  { id: 'preview', size: 68, minSize: 38 },
];

export const createLayoutSplittersSlice = ({
  set,
}: {
  set: StoreApi<LayoutState>['setState'];
}): LayoutSplittersSlice => ({
  // Main panel sizes - default layout
  mainPanelSizes: [
    { id: 'domain-tree', size: 20 },
    { id: 'main', size: 55 },
    { id: 'chat', size: 25 },
  ],

  // Global requests splitter sizes - default layout
  globalRequestsSplitterSizes: [
    { id: 'table', size: 30 },
    { id: 'details', size: 70 },
  ],

  // WebSocket splitter sizes - default layout
  webSocketSplitterSizes: [50, 50] as [number, number],

  // Interact splitter sizes - default layout
  interactSplitterSizes: [50, 50] as [number, number],

  // Intercept splitter sizes - default layout
  interceptSplitterSizes: [
    { id: 'request', size: 50, minSize: 25 },
    { id: 'response', size: 50, minSize: 25 },
  ],

  // Replay splitter sizes - default layout
  replaySplitterSizes: [
    { id: 'request', size: 50 },
    { id: 'response', size: 50 },
  ],

  replayCollectionsPanelSizes: [
    { id: 'collections', size: 18 },
    { id: 'main', size: 82 },
  ],

  // Requests content splitter sizes - default layout
  requestsContentSplitterSizes: [
    { id: 'table', size: 30 },
    { id: 'details', size: 70 },
  ],

  selectedRequestsContentId: null,

  // Request viewer splitter sizes - default layout
  requestViewerSplitterSizes: [
    { id: 'request', size: 50 },
    { id: 'response', size: 50 },
  ],

  matchReplaceRequestsPreviewSplitterSizes:
    DEFAULT_MATCH_REPLACE_REQUESTS_PREVIEW_SPLITTER_SIZES,

  matchReplaceOriginalTransformedSplitterSizes:
    DEFAULT_MATCH_REPLACE_ORIGINAL_TRANSFORMED_SPLITTER_SIZES,

  matchReplaceRulesEditorSplitterSizes:
    DEFAULT_MATCH_REPLACE_RULES_EDITOR_SPLITTER_SIZES,

  matchReplaceRulesPreviewSplitterSizes:
    DEFAULT_MATCH_REPLACE_RULES_PREVIEW_SPLITTER_SIZES,

  setMainPanelSizes: (sizes) => set({ mainPanelSizes: sizes }),

  setGlobalRequestsSplitterSizes: (sizes) =>
    set({ globalRequestsSplitterSizes: sizes }),

  setWebSocketSplitterSizes: (sizes) => set({ webSocketSplitterSizes: sizes }),

  setInteractSplitterSizes: (sizes: [number, number]) =>
    set({ interactSplitterSizes: sizes }),

  setInterceptSplitterSizes: (sizes) => set({ interceptSplitterSizes: sizes }),

  setReplaySplitterSizes: (sizes) => set({ replaySplitterSizes: sizes }),

  setReplayCollectionsPanelSizes: (sizes) =>
    set({ replayCollectionsPanelSizes: sizes }),

  setRequestsContentSplitterSizes: (sizes) =>
    set({ requestsContentSplitterSizes: sizes }),

  setSelectedRequestsContentId: (id) => set({ selectedRequestsContentId: id }),

  setRequestViewerSplitterSizes: (sizes) =>
    set({ requestViewerSplitterSizes: sizes }),

  setMatchReplaceRequestsPreviewSplitterSizes: (sizes) =>
    set({ matchReplaceRequestsPreviewSplitterSizes: sizes }),

  setMatchReplaceOriginalTransformedSplitterSizes: (sizes) =>
    set({ matchReplaceOriginalTransformedSplitterSizes: sizes }),

  setMatchReplaceRulesEditorSplitterSizes: (sizes) =>
    set({ matchReplaceRulesEditorSplitterSizes: sizes }),

  setMatchReplaceRulesPreviewSplitterSizes: (sizes) =>
    set({ matchReplaceRulesPreviewSplitterSizes: sizes }),
});
